"use client";

import { useState } from "react";
import { Calendar, Clock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import toast from "react-hot-toast";

interface ReservationMessageProps {
  content: string;
  isCurrentUserSeller: boolean; // 현재 사용자가 판매자인지
  onAccept?: (reservationId: string) => Promise<void>;
  onReject?: (reservationId: string) => Promise<void>;
}

export default function ReservationMessage({
  content,
  isCurrentUserSeller,
  onAccept,
  onReject,
}: ReservationMessageProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  // 예약 정보 파싱
  const parseReservationInfo = (content: string) => {
    try {
      const lines = content.split("\n");
      let date = "";
      let time = "";
      let memo = "";
      let reservationId = "";
      let status = "pending";

      lines.forEach((line) => {
        if (line.startsWith("날짜:")) {
          date = line.replace("날짜:", "").trim();
        } else if (line.startsWith("시간:")) {
          time = line.replace("시간:", "").trim();
        } else if (line.startsWith("메모:")) {
          memo = line.replace("메모:", "").trim();
        } else if (line.startsWith("reservationId:")) {
          reservationId = line.replace("reservationId:", "").trim();
        } else if (line.startsWith("status:")) {
          status = line.replace("status:", "").trim();
        }
      });

      return { date, time, memo, reservationId, status };
    } catch (error) {
      console.error("예약 정보 파싱 실패:", error);
      return null;
    }
  };

  const reservationInfo = parseReservationInfo(content);

  if (!reservationInfo) {
    return <div className="text-gray-500">예약 정보를 불러올 수 없습니다.</div>;
  }

  const { date, time, memo, reservationId, status } = reservationInfo;

  const handleAccept = async () => {
    if (!onAccept || !reservationId) return;

    setIsProcessing(true);
    try {
      await onAccept(reservationId);
      toast.success("예약을 수락했습니다!");
    } catch (error) {
      console.error("예약 수락 실패:", error);
      toast.error("예약 수락에 실패했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!onReject || !reservationId) return;

    setIsProcessing(true);
    try {
      await onReject(reservationId);
      toast.success("예약을 거절했습니다.");
    } catch (error) {
      console.error("예약 거절 실패:", error);
      toast.error("예약 거절에 실패했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 space-y-3 shadow-sm">
      {/* 헤더 */}
      <div className="flex items-center gap-2">
        <Calendar className="w-5 h-5 text-blue-600" />
        <h3 className="font-bold text-blue-900">거래 예약 요청</h3>
      </div>

      {/* 예약 정보 */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700">
            <span className="font-semibold">날짜:</span> {date}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700">
            <span className="font-semibold">시간:</span> {time}
          </span>
        </div>
        {memo && (
          <div className="text-sm">
            <span className="font-semibold text-gray-700">메모:</span>
            <p className="text-gray-600 mt-1 bg-white/50 rounded-lg p-2">
              {memo}
            </p>
          </div>
        )}
      </div>

      {/* 상태 표시 */}
      {status === "accepted" && (
        <div className="flex items-center gap-2 text-green-700 bg-green-100 rounded-lg p-2">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">예약 수락됨</span>
        </div>
      )}

      {status === "rejected" && (
        <div className="flex items-center gap-2 text-red-700 bg-red-100 rounded-lg p-2">
          <XCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">예약 거절됨</span>
        </div>
      )}

      {/* 수락/거절 버튼 - 판매자에게만, pending 상태일 때만 표시 */}
      {isCurrentUserSeller && status === "pending" && (
        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleReject}
            variant="outline"
            size="sm"
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-1" />
                거절
              </>
            )}
          </Button>
          <Button
            onClick={handleAccept}
            size="sm"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                수락
              </>
            )}
          </Button>
        </div>
      )}

      {/* 요청자에게는 대기 메시지 표시 */}
      {!isCurrentUserSeller && status === "pending" && (
        <div className="text-sm text-gray-600 text-center py-2 bg-white/50 rounded-lg">
          상대방의 응답을 기다리고 있습니다...
        </div>
      )}
    </div>
  );
}

